import { useEffect, useMemo, useState } from 'react';
import { useStudents } from '../data/useStudents';
import { useCategories } from '../data/useCategories';
import { useTags } from '../data/useTags';
import { defaultEventCategory, SWATCHES } from '../domain/categories';
import { freeId } from '../domain/freeId';
import { TAG_KINDS, tagKind, tagMembers } from '../domain/tags';
import { coreSync } from '../lib/core';
import { clampAmountDecimals } from '../domain/money';
import { useCurrency } from '../context/LocaleContext';
import {
  addStudentAsksForParent, addStudentFamilyFromStudent, addStudentFamilyStepReady, addStudentNewFamily,
  emailError, rateError,
} from '../domain/people';
import { durationError, DURATION_MAX_MINUTES } from '../domain/eventSeries';

// The Add New Student wizard's state: the form, which step it is on, and the
// one save that creates the student and their family together.

export const STEPS = ['Student Details', 'Family Details', 'Lesson Settings', 'Confirmation'];
export const CONFIRMATION_STEP = 3;

const emptyForm = () => ({
  studentType: 'child', status: 'active',
  firstName: '', lastName: '',
  studentSince: '', gender: '', birthday: '', school: '', referrer: '',
  skillLevel: '', note: '',
  // The studio's tags this student will be filed under (#138): ids, because
  // membership lives on the tag document and Save writes a membership.
  // `newTags` holds the ones created inline, which are not records yet — see
  // `save()` for why they are minted here and written there.
  tagIds: [], newTags: [],
  familyChoice: 'new', existingFamilyId: '',
  // The new family's first contact — the parent's, or for an adult the
  // student's own (core/src/add_student.rs). No address lives on the student
  // (#792).
  parentFirstName: '', parentLastName: '', parentEmail: '', parentPhone: '', parentSms: false,
  // Parent Lesson Reminders, both off as MMS's are on a new contact: a family
  // that has not asked to be messaged has not asked. They are the contact's
  // `emailReminders` / `smsReminders`, the per-person half of a lesson
  // reminder's gates.
  parentEmailReminders: false, parentSmsReminders: false,
  // The family's time zone, asked for on the family step because that is where
  // MMS asks for it and it applies to the whole account (see FamilyStep.jsx).
  // '' is "the studio's", the null the document holds. Kept in the form
  // whether or not Multi-Time Zone Selection is on, so the form's shape does
  // not change with a setting.
  familyTimeZone: '',
  // The category is a pick from the studio's list, not a string (#110). Both
  // halves are stored, the pair `chooseCategory` writes on the event form: the
  // id is what a rename, a recolour and a reminder lead reach, and the label is
  // what every screen renders without joining a second collection.
  //
  // Empty here rather than 'lesson': the studio's categories are not known
  // when this object is built. The effect below seeds it once the list is in,
  // and until then Save is gated on it.
  categoryId: '', categoryLabel: '', durationMin: 30,
  pricingMode: coreSync().defaultPricingMode(), rate: '30.00',
});

export default function useAddStudentForm({ onClosed }) {
  // The writer only; nothing here reads the roster (#315).
  const { createStudent } = useStudents();
  // The studio's managed tags, for the two pickers on step 1 (#138). `loaded`
  // is "this studio's collection has been read", which `saveTag` checks before
  // writing: a membership written against a collection this app has not seen
  // could land on the previously signed-in user's tags.
  const { tags, loaded: tagsLoaded, saveTag } = useTags();
  // The studio's own categories, so Step 3 can offer them (#110). Falls back to
  // the built-in six for a studio that has never saved any, as the event form's
  // select does. `loaded` is "this studio's collection has been read", which is
  // not "categories is non-empty" — see the seeding effect below.
  const { categories, loaded } = useCategories();
  // The rate field is typed in the studio's currency (src/domain/locale.js).
  const currency = useCurrency();

  const [form, setForm] = useState(emptyForm);
  // Whether the teacher has picked a category themselves: what stops the
  // seeding effect re-deriving the default. A fact about the teacher, because
  // the form holds a value the moment the effect seeds one.
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [created, setCreated] = useState(null);

  // Rate truncates a third decimal rather than rounding it (#308):
  // `fromDecimal` rounds half-up, so `50.999` would create the student on a
  // $51.00 rate. `clampAmountDecimals` is MMS's own editor on its Add Payment
  // drawer (#246): the third keystroke is swallowed. Keyed on `rate`, as `set`
  // in src/hooks/useEventForm.js is on `priceOverride`: this setter carries
  // names, dates and tag ids too.
  const set = (key, value) => setForm((p) => ({
    ...p,
    [key]: key === 'rate' ? clampAmountDecimals(value, currency) : value,
  }));

  // ── The two tag pickers on step 1 (#138) ──────────────────────────────────
  //
  // The studio's tags plus the ones created inline and not yet written, as one
  // list, so the picker renders a just-created tag beside the stored ones.
  const allTags = useMemo(() => [...tags, ...form.newTags], [tags, form.newTags]);
  const selectedTagIds = useMemo(() => new Set(form.tagIds), [form.tagIds]);
  const tagsOfKind = (kind) => allTags.filter((t) => tagKind(t) === kind);

  const toggleTag = (id) => setForm((f) => ({
    ...f,
    tagIds: f.tagIds.includes(id) ? f.tagIds.filter((x) => x !== id) : [...f.tagIds, id],
  }));

  // Create inline — MMS's `Create Instrument Tag` / `Create Group Tag`.
  //
  // The id is minted here and the record written on Save: `saveTag` returns
  // nothing, so a tag created and selected in the same breath would have no id
  // to select by; it accepts one. Abandoning the wizard leaves no orphan tag.
  const createTag = (label, kind) => {
    const id = freeId('tag');
    setForm((f) => ({
      ...f,
      newTags: [...f.newTags, {
        id,
        label: label.trim(),
        kind: kind === TAG_KINDS.group ? TAG_KINDS.group : TAG_KINDS.instrument,
        // MMS's create form defaults the colour rather than asking for one;
        // the palette is on the Tags tab, where a tag is edited.
        color: SWATCHES[0],
        description: '',
        studentIds: [],
      }],
      tagIds: [...f.tagIds, id],
    }));
  };

  // Pick a category by id and store its name alongside (#110, #128), as
  // `chooseCategory` in hooks/useEventForm.js does. A label cannot tell two
  // categories of one name apart.
  const chooseCategory = (id) => {
    const chosen = categories.find((c) => c.id === id) || null;
    setCategoryTouched(true);
    setForm((f) => ({ ...f, categoryId: chosen?.id || '', categoryLabel: chosen?.label || '' }));
  };

  // Seed the picker from the studio's list.
  //
  // Not a default in `emptyForm`: the list is read asynchronously, and a
  // studio that has never opened the category editor is shown
  // `DEFAULT_CATEGORIES`, which are offered, not stored.
  //
  // Which one is `defaultEventCategory`, by id (#141): the same answer the
  // event form opens on, falling back to the first category of a studio that
  // deleted the built-in Lesson. `categories[0]` would change with the list's
  // order, which differs between the built-ins and a stored list.
  //
  // The guard is "has the teacher chosen", not "is there a value".
  // `useCategories` hands out `DEFAULT_CATEGORIES` as a placeholder before it
  // has read this studio's collection, and seeding from it would store a stale
  // label or an id the studio does not have. `loaded` is not enough on its
  // own: the first snapshot of a fresh listener is routinely `n=0,
  // fromCache=true` although the collection is not empty, with the server's
  // documents a tick later. So the effect re-derives on every list until the
  // teacher picks something; `chooseCategory` is the only thing that stops it,
  // and `addAnother` clears the flag with the rest of the form. `loaded` stays
  // in the condition so the placeholder is never written even once.
  useEffect(() => {
    if (categoryTouched || !loaded || !categories.length) return;
    const seed = defaultEventCategory(categories);
    if (!seed) return;
    setForm((f) => (
      f.categoryId === seed.id && f.categoryLabel === seed.label
        ? f
        : { ...f, categoryId: seed.id, categoryLabel: seed.label }
    ));
  }, [categories, loaded, categoryTouched]);

  // "Add Another Student" keeps the family and clears everything about the
  // student: adding siblings is the reason to click it.
  //
  // When the first student created a new family, the sibling joins that family
  // by id rather than describing an identical one again, which would produce
  // two family accounts with the same surname and split the balance.
  const addAnother = () => {
    const familyId = created?.familyId || null;
    setForm({
      ...emptyForm(),
      ...(familyId ? { familyChoice: 'existing', existingFamilyId: familyId } : {}),
    });
    setStep(0);
    setCreated(null);
    setCategoryTouched(false);
    setShowMore(false);
  };

  const reset = () => {
    setForm(emptyForm());
    setStep(0);
    setCreated(null);
    setShowMore(false);
  };

  const close = () => { reset(); onClosed?.(); };

  // A phone number that cannot be texted cannot leave the step it was typed on
  // (#90): PhoneField shows the error, and this stops Next being pressed
  // through it. MMS has no such check and stores whatever it is handed; the
  // divergence is recorded in src/domain/phone.js.
  //
  // Empty still advances: every phone field here is optional, and what is
  // refused is a number that is present and unusable. A well-formed number
  // nobody answers is not caught anywhere (src/domain/phone.js).
  //
  // The tag pickers are not gated here: Save writes memberships on the tag
  // documents (#138, #328), whose `studentIds` cap of 500 is not reachable by
  // ticking boxes.

  // And the email box (#120). `optionalEmail` in firestore.rules matches
  // `^[^@\s]+@[^@\s]+$` and bounds the length, so a name typed into the box,
  // or two addresses pasted into one, would fail as a bare permission error
  // naming no field (#115). It is the new contact's, on step 1.
  const parentEmailError = emailError(form.parentEmail);

  // And the Duration box on step 3 (#137, #149). `defaultDurationMin` is
  // `requiredIntInRange(d, k, 1, 1440)` in firestore.rules, so 5,000 or 37.5
  // is refused at the field rather than by the rules. The argument for whole
  // minutes is at `durationError` in domain/eventSeries.js.
  //
  // Required (#352): MMS refuses an empty Duration on create and on edit, and
  // a make-up credit is divided by this number.
  const durationOk = !durationError(form.durationMin, DURATION_MAX_MINUTES, { required: true });

  // The Rate box on step 3 (#164). `rateMinor` reaches firestore.rules'
  // `optionalMinor`, which carries the same ceiling `validLedgerEntry` does
  // (see the note beside `optionalMinor` there) and refuses a negative, so the
  // check is the rate's own (`people::rate_error`), as on the Lesson Settings
  // drawer. Skipped when pricing is 'none': the field is not shown and
  // `save()` writes 0.
  const rateErrorText = form.pricingMode === 'none' ? null : rateError(form.rate, currency);
  const rateOk = !rateErrorText;

  // ── The Family step is the core's, and an adult is their own family ──────
  //
  // Whether it asks for a parent, when it may be left and what a new family is
  // made from are `core/src/add_student.rs`, so the two phones' wizards answer
  // as this one does. In MMS an Adult student joining *A new family* is asked
  // for nothing on Family Details but the zone, and the account is made from
  // the student — named after them, with them as its contact and invoice
  // recipient. Here the step asks an adult for their own email, number and
  // Lesson Reminders ticks, the contact's; the core module's header has where
  // else we knowingly differ.
  const asksForParent = addStudentAsksForParent(form);
  const adult = addStudentFamilyFromStudent(form.studentType);
  const familyStepReady = addStudentFamilyStepReady(form);

  const canAdvance =
    step === 0 ? Boolean(form.firstName.trim() && form.lastName.trim())
    : step === 1 ? familyStepReady
    // Step 3 needs a category id (#110). The list falls back to the built-in
    // six, so the effect above seeds the picker at once; this covers the
    // window where it has not.
    : step === 2 ? Boolean(form.categoryId) && durationOk && rateOk
    : true;

  // Every picked tag, written with this student added to it.
  //
  // A tag created inline has no record yet, so it is written whole — label,
  // kind, colour, description, and the one member. A tag that already exists is
  // written with its stored fields unchanged and the new student appended;
  // `saveTag` turns that into an `arrayUnion` of the difference, so the other
  // members are never sent and cannot be lost.
  //
  // `save()` gates on `tagsLoaded` before `createStudent`, so a wizard that
  // cannot attach the tags creates nobody. A tag created inline needs no list,
  // so it is the path that could reach here with `tagsLoaded` false. This
  // throws rather than returning: `save()` awaits it before `setCreated`, so a
  // throw leaves the wizard on step 3 with Save pressable rather than showing
  // the tick over an untagged student.
  const attachTags = async (studentId) => {
    // Nothing ticked, nothing created: no membership to write, so whether the
    // collection has been read does not matter.
    if (!form.tagIds.length) return;
    if (!tagsLoaded) {
      throw new Error('attachTags: the tags collection has not been read — save() gates this');
    }
    const byId = new Map(allTags.map((t) => [t.id, t]));
    for (const id of form.tagIds) {
      const tag = byId.get(id);
      if (!tag) continue;
      // Sequential: each is its own batch, and a failure part way should stop
      // rather than fire the rest at a collection that has just refused one.
      await saveTag({
        id: tag.id,
        label: tag.label,
        kind: tagKind(tag),
        color: tag.color,
        description: tag.description || '',
        studentIds: [...tagMembers(tag), studentId],
      });
    }
  };

  const save = async () => {
    // `canAdvance` gates the step the contact's number and address are typed
    // on, but Save is pressed from the confirmation step, and a teacher can
    // step back, break a value and step forward again. So the Family step's
    // own question is asked again (#90, #120); fields it does not show (an
    // existing family's) are not judged: they are not written.
    if (!familyStepReady) return;
    // The duration and the rate are re-checked for the same reason (#137,
    // #164).
    if (!durationOk || !rateOk) return;
    // And the tags, a gate on the data rather than on the form: `saveTag`
    // refuses a write against a collection this app has not read
    // (useTags.jsx). Refused here, before anything is created, so the teacher
    // can press Save again a tick later rather than a student being created and
    // the tags dropped. `tagsPending` is the same condition, so the drawer can
    // say why Save did nothing; this refusal has no field of its own.
    if (form.tagIds.length && !tagsLoaded) return;
    setSaving(true);
    try {
      // The new family and its first contact, or two nulls (see the core's
      // `new_family`). For a child they are the parent the Family step
      // collected, under the parent's surname or the student's; for an adult
      // they are the student, as MMS makes them. The contact carries the
      // step's Lesson Reminders ticks.
      const { family, contact } = addStudentNewFamily(form);
      // Both documents go through the normalisers in domain/people.js; this
      // says which form value feeds which field. Trimming and empty-to-null
      // are the normaliser's job.
      const res = await createStudent({
        student: {
          firstName: form.firstName, lastName: form.lastName,
          studentType: form.studentType, status: form.status,
          studentSince: form.studentSince, gender: form.gender, birthday: form.birthday,
          school: form.school, referrer: form.referrer, skillLevel: form.skillLevel,
          // Tags are written below as memberships on the tag (#138).
          note: form.note,
          defaultDurationMin: form.durationMin,
          // Both halves; the id is what a rename, a recolour or a
          // reminder-lead change can reach (#110).
          categoryId: form.categoryId,
          categoryLabel: form.categoryLabel,
          pricingMode: form.pricingMode,
          rateMinor: form.pricingMode === 'none' ? 0 : coreSync().fromDecimal(form.rate, currency),
          familyId: form.familyChoice === 'existing' ? form.existingFamilyId : null,
        },
        // The family's own name, which the invoice is addressed to — distinct
        // from the contact person below, though a new family defaults one from
        // the other.
        family,
        // The account's first `familyContacts` record (#319). `mobile`, not
        // `phone`: a contact carries Mobile, Home and Work with an SMS tick
        // each (#176), and the one asked for is the one messages go to
        // (`address_on` in core/src/messages.rs reads `mobile`).
        contact,
      });
      // ── The memberships, after the student exists and before we say so ────
      //
      // Membership lives on the tag (#138), so attaching a new student is a
      // write to the tags collection keyed on a `studentId` that does not
      // exist until `createStudent` returns; it cannot share the batch. It is
      // awaited before `setCreated`, so a failure throws out of `save()` with
      // the wizard on step 3 and Save pressable.
      //
      // Two transactions, so a student can land without their tags if the
      // second fails. That is visible — on the roster with no tags — and
      // repairable from Students → Tags.
      //
      // `saveTag` writes an `arrayUnion` delta (#198), so this cannot clobber a
      // membership another device added while the wizard was open.
      if (res?.studentId) await attachTags(res.studentId);

      setCreated(res);
      setStep(CONFIRMATION_STEP);
    } finally {
      setSaving(false);
    }
  };

  return {
    form, set, step, setStep, saving, showMore, setShowMore,
    created, canAdvance, save, reset, addAnother, close,
    // Step 1's tag pickers (#138): the vocabulary, what is ticked, and the two
    // ways to change it. `tags` includes anything created inline, so the
    // duplicate check in `TagPickerField` sees a name added a moment ago.
    tags: allTags, tagsOfKind, selectedTagIds, toggleTag, createTag,
    // Whether the studio's tags have been read: "this studio has no tags" and
    // "this list has not arrived" are the same empty array and not the same
    // sentence (`TagPickerField`).
    tagsLoaded,
    // "Save would drop the tags, so it will not run." The same fact as the
    // gate in `save()`, for the footer: the window before the tags snapshot
    // lands, reachable because a tag created inline needs no snapshot.
    tagsPending: Boolean(form.tagIds.length) && !tagsLoaded,
    // Step 3's category picker: the list to offer, and the setter that writes
    // the id and the label together (#110).
    categories, chooseCategory,
    // Step 3's Rate field message, computed here because this hook has the
    // currency the figure is typed in (#164).
    rateError: rateErrorText,
    // The email message, computed here so the gate above and the field below
    // cannot disagree about what is wrong (#120).
    parentEmailError,
    // Whether the Family step shows the parent's name — not for an adult's new
    // family, which is named after the student — and whether the student is
    // an adult, whose own the contact's fields are.
    asksForParent, adult,
  };
}
